import { useState, useEffect, useRef, useMemo } from "react";
import { loadFromSupabase } from "../supabaseDB";

/* ═══════════════════════════════════════════════════════════════
   GlobalSearch — Xero-style command palette
   Reads localStorage keys directly. Click → navigate + open record.
   Esc closes. Debounced 300ms.
   ═══════════════════════════════════════════════════════════════ */

function loadLS(k, fb) {
  try { return JSON.parse(localStorage.getItem(k) || JSON.stringify(fb)); }
  catch { return fb; }
}
function fmtMoney(n) {
  var v = parseFloat(n) || 0;
  return "$" + v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function calcBillSubtotal(bill) {
  var grossSales = 0;
  (bill.lines || []).forEach(function (l) {
    if (l.saleStatus !== "dumped" && l.saleStatus !== "donated" && l.saleStatus !== "samples" && l.saleStatus !== "transfer") {
      grossSales += (parseInt(l.cases, 10) || 0) * (parseFloat(l.reportedPrice) || 0);
    }
  });
  var totalFees = 0;
  (bill.fees || []).forEach(function (f) { totalFees += parseFloat(f.amount) || 0; });
  var commAmt = grossSales * ((parseFloat(bill.commissionRate) || 0) / 100);
  return grossSales - commAmt - totalFees;
}

function calcInvoiceTotal(inv) {
  var total = 0;
  (inv.lines || []).forEach(function (l) {
    if (l.saleStatus !== "dumped" && l.saleStatus !== "donated" && l.saleStatus !== "samples" && l.saleStatus !== "transfer") {
      total += (parseInt(l.cases, 10) || 0) * (parseFloat(l.salePrice) || 0);
    }
  });
  var tFees = 0;
  (inv.fees || []).forEach(function (fe) { tFees += parseFloat(fe.amount) || 0; });
  return total - tFees;
}

function contains(value, q) { return String(value || "").toLowerCase().includes(q); }

function searchAll(rawQ) {
  if (!rawQ) return [];
  var q = rawQ.toLowerCase().trim();
  if (!q) return [];
  var groups = [];

  /* ───── SHIPMENTS ───── */
  var ships = loadLS("gfi-shipments", []).filter(function (s) {
    return contains(s.lot, q) || contains(s.shipperName, q) ||
           contains(s.proforma, q) || contains(s.shipperInvoice, q) ||
           contains(s.entryNumber, q);
  });
  if (ships.length) groups.push({
    type: "shipments", module: "shipments", openFn: "__gfiShipmentOpen",
    items: ships.map(function (s) {
      var cases = (s.lines || []).reduce(function (sum, l) { return sum + (parseInt(l.cases, 10) || 0); }, 0);
      return {
        id: s.id,
        primary: "LOT " + (s.lot || "—"),
        secondary: s.shipperName || "—",
        tertiary: s.proforma || s.shipperInvoice || "",
        meta: s.status,
        amountLabel: cases ? cases + " cases" : null,
      };
    }),
  });

  /* ───── BILLS ───── */
  var bills = loadLS("gfi-bills", []).filter(function (b) {
    return contains(b.lot, q) || contains(b.shipperName, q) || contains(b.reference, q) ||
           contains(b.id, q) || contains(b.proforma, q) || contains(b.shipperInvoice, q);
  });
  if (bills.length) groups.push({
    type: "bills", module: "bills", openFn: "__gfiBillOpen",
    items: bills.map(function (b) {
      var subtotal = calcBillSubtotal(b);
      var due = subtotal - (parseFloat(b.amountPaid) || 0);
      return {
        id: b.id,
        primary: b.id || "—",
        secondary: b.shipperName || "—",
        tertiary: "LOT " + (b.lot || "—") + (b.proforma ? " · " + b.proforma : ""),
        amount: due,
        meta: b.status,
      };
    }),
  });

  /* ───── INVOICES ───── */
  var invoices = loadLS("gfi-invoices", []).filter(function (i) {
    return contains(i.puNumber, q) || contains(i.clientName, q) || contains(i.lot, q) ||
           contains(i.clientRef, q) || contains(i.id, q);
  });
  if (invoices.length) groups.push({
    type: "invoices", module: "invoices", openFn: "__gfiInvoiceOpen",
    items: invoices.map(function (i) {
      var total = calcInvoiceTotal(i);
      var due = total - (parseFloat(i.amountPaid) || 0);
      return {
        id: i.id,
        primary: "PU " + (i.puNumber || "—"),
        secondary: i.clientName || "—",
        tertiary: "LOT " + (i.lot || "—") + (i.clientRef ? " · " + i.clientRef : ""),
        amount: due,
        meta: i.status,
      };
    }),
  });

  /* ───── EXPENSE BILLS ───── */
  var eb = loadLS("gfi-expense-bills", []).filter(function (b) {
    return contains(b.vendor, q) || contains(b.reference, q) || contains(b.id, q) ||
           contains(b.entryNumber, q);
  });
  if (eb.length) groups.push({
    type: "expense_bills", module: "expense_bills", openFn: "__gfiExpenseBillOpen",
    items: eb.map(function (b) {
      var paid = (b.payments || []).reduce(function (s, p) { return s + (parseFloat(p.amount) || 0); }, 0);
      var balance = (parseFloat(b.amount) || 0) - paid;
      return {
        id: b.id,
        primary: b.id || "—",
        secondary: b.vendor || "—",
        tertiary: b.reference || "",
        amount: balance,
      };
    }),
  });

  /* ───── SUPPLIERS ───── */
  var sups = loadLS("gfi-suppliers", []).filter(function (s) {
    return contains(s.shortName, q) || contains(s.fullName, q) ||
           contains(s.initials, q) || contains(s.companyName, q);
  });
  if (sups.length) groups.push({
    type: "suppliers", module: "suppliers", openFn: "__gfiSupplierOpen",
    items: sups.map(function (s) {
      return {
        id: s.id,
        primary: s.initials || "—",
        secondary: s.shortName || s.fullName || s.companyName || "—",
        tertiary: [s.city, s.country].filter(Boolean).join(", "),
      };
    }),
  });

  /* ───── CLIENTS ───── */
  var clients = loadLS("gfi-clients", []).filter(function (c) {
    return contains(c.shortName, q) || contains(c.fullName, q) ||
           contains(c.dba, q) || contains(c.initials, q);
  });
  if (clients.length) groups.push({
    type: "clients", module: "clients", openFn: "__gfiClientOpen",
    items: clients.map(function (c) {
      return {
        id: c.id,
        primary: c.initials || "—",
        secondary: c.shortName || c.fullName || "—",
        tertiary: c.dba || [c.city, c.state].filter(Boolean).join(", "),
      };
    }),
  });

  /* ───── PAYMENTS ───── */
  var pays = loadLS("gfi-payments", []).filter(function (p) {
    return contains(p.id, q) || contains(p.reference, q) || contains(p.shipperName, q) ||
           (p.allocations || []).some(function (a) {
             return contains(a.shipperName, q) || contains(a.clientName, q) || contains(a.vendor, q);
           });
  });
  if (pays.length) groups.push({
    type: "payments", module: "payments", openFn: "__gfiPaymentOpen",
    items: pays.map(function (p) {
      var total = (p.allocations || []).reduce(function (s, a) { return s + (parseFloat(a.amount) || 0); }, 0);
      var party = p.shipperName || (p.allocations || []).map(function (a) { return a.shipperName || a.clientName || a.vendor; }).filter(Boolean).join(", ");
      return {
        id: p.id,
        primary: p.id,
        secondary: party || "—",
        tertiary: p.reference || "",
        amount: total,
        meta: p.type || "outbound",
      };
    }),
  });

  return groups;
}

const GROUP_LABELS = {
  en: { shipments: "SHIPMENTS", bills: "BILLS", invoices: "INVOICES", expense_bills: "EXPENSE BILLS", suppliers: "SUPPLIERS", clients: "CLIENTS", payments: "PAYMENTS" },
  es: { shipments: "EMBARQUES", bills: "BILLS", invoices: "FACTURAS", expense_bills: "FACTURAS DE GASTOS", suppliers: "PROVEEDORES", clients: "CLIENTES", payments: "PAGOS" },
};

const GROUP_COLORS = {
  shipments: "#2A7A6F",
  bills: "#1B5A52",
  invoices: "#c65d00",
  expense_bills: "#B45309",
  suppliers: "#6B7280",
  clients: "#374151",
  payments: "#1B3A4B",
};

const MAX_PER_GROUP = 8;

export default function GlobalSearch({ lang, onClose }) {
  var [query, setQuery] = useState("");
  var [debounced, setDebounced] = useState("");
  var inputRef = useRef(null);

  /* 300ms debounce */
  useEffect(function () {
    var t = setTimeout(function () { setDebounced(query); }, 300);
    return function () { clearTimeout(t); };
  }, [query]);

  /* Escape closes */
  useEffect(function () {
    var onKey = function (e) { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return function () { window.removeEventListener("keydown", onKey); };
  }, [onClose]);

  /* Auto-focus on mount */
  useEffect(function () {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  /* Preload from Supabase so the legacy `gfi-*` caches that
     `searchAll()` scans synchronously are fresh. Bumps `preloadKey`
     once warm so `useMemo` re-runs with the new data. */
  var [preloadKey, setPreloadKey] = useState(0);
  useEffect(function () {
    var alive = true;
    var pairs = [
      ["shipments",     "gfi-shipments"],
      ["bills",         "gfi-bills"],
      ["invoices",      "gfi-invoices"],
      ["expense_bills", "gfi-expense-bills"],
      ["suppliers",     "gfi-suppliers"],
      ["clients",       "gfi-clients"],
      ["payments",      "gfi-payments"],
    ];
    Promise.all(pairs.map(function (p) {
      return loadFromSupabase(p[0]).then(function (rows) {
        try { localStorage.setItem(p[1], JSON.stringify(rows)); } catch (e) {}
      }).catch(function () {});
    })).then(function () { if (alive) setPreloadKey(function (k) { return k + 1; }); });
    return function () { alive = false; };
  }, []);

  var groups = useMemo(function () { return searchAll(debounced); }, [debounced, preloadKey]);
  var total = groups.reduce(function (s, g) { return s + g.items.length; }, 0);
  var labels = GROUP_LABELS[lang === "es" ? "es" : "en"];
  var hasQuery = debounced.trim().length > 0;

  function openResult(group, item) {
    if (window.__gfiNavigate) window.__gfiNavigate(group.module);
    if (group.openFn) {
      setTimeout(function () {
        var fn = window[group.openFn];
        if (typeof fn === "function") fn(item.id);
      }, 220);
    }
    onClose();
  }

  return (
    <div className="gs-backdrop" onClick={onClose}>
      <div className="gs-panel" onClick={function (e) { e.stopPropagation(); }}>
        <div className="gs-input-row">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6a7a90" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            placeholder={lang === "es" ? "Buscar embarques, facturas, clientes, pagos…" : "Search shipments, invoices, clients, payments…"}
            value={query}
            onChange={function (e) { setQuery(e.target.value); }}
            className="gs-input"
          />
          {query && (
            <button className="gs-clear" onClick={function () { setQuery(""); inputRef.current && inputRef.current.focus(); }} title="Clear">×</button>
          )}
          <span className="gs-esc">esc</span>
        </div>

        <div className="gs-results">
          {!hasQuery && (
            <div className="gs-hint">
              {lang === "es" ? "Escribe para buscar en todos los módulos…" : "Type to search across all modules…"}
            </div>
          )}
          {hasQuery && total === 0 && (
            <div className="gs-hint">
              {lang === "es" ? "Sin resultados para " : "No results for "}<strong>"{debounced}"</strong>
            </div>
          )}
          {groups.map(function (group) {
            var visible = group.items.slice(0, MAX_PER_GROUP);
            var hidden = group.items.length - visible.length;
            return (
              <div key={group.type} className="gs-group">
                <div className="gs-group-label" style={{ color: GROUP_COLORS[group.type] }}>
                  {labels[group.type]} <span className="gs-group-count">({group.items.length})</span>
                </div>
                {visible.map(function (item) {
                  return (
                    <button
                      key={item.id}
                      className="gs-item"
                      onClick={function () { openResult(group, item); }}
                    >
                      <span className="gs-item-primary">{item.primary}</span>
                      <span className="gs-item-secondary">{item.secondary}</span>
                      <span className="gs-item-tertiary">{item.tertiary}</span>
                      <span className="gs-item-amount">
                        {item.amount !== undefined ? fmtMoney(item.amount) : (item.amountLabel || "")}
                      </span>
                    </button>
                  );
                })}
                {hidden > 0 && (
                  <div className="gs-more">+ {hidden} {lang === "es" ? "más" : "more"}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
